import { invalidateListForMovedEntity } from "@/api/query-cache/invalidate-on-legacy-writes";
import { deviceMarkerUpdateEvent } from "@/services/map-management/marker-invalidation";
import { useStore } from "@/store/store";

import type MapEntityBase from "./map-entity-base";

interface PositionedDeviceModel {
    lat?: number;
    long?: number;
    onChange?: {
        addListener: (hook: () => void, uid?: string) => string;
    };
}

interface PositionedMapEntity extends MapEntityBase {
    lat: number;
    long: number;
    model: PositionedDeviceModel;
}

/**
 * Copies device model coordinates onto the map entity and refetches the list that positions its marker.
 *
 * The marker is drawn from the DTO, not from the entity, so writing the entity alone leaves a
 * repositioned device at its old coordinates. Skipping the debounce is what keeps that gap to one
 * request: a reposition is a discrete save, and the user is watching for the marker to move.
 */
export const syncMapEntityPositionFromModel = (mapEntity: PositionedMapEntity) => {
    const { lat, long } = mapEntity.model;
    if (mapEntity.lat === lat && mapEntity.long === long) {
        return;
    }

    // Skips the entity currently being repositioned so we don't fight it
    const { isRepositioningDevice, selectedMapEntity } = useStore.getState();
    if (isRepositioningDevice && selectedMapEntity === mapEntity) {
        return;
    }

    mapEntity.lat = lat ?? mapEntity.lat;
    mapEntity.long = long ?? mapEntity.long;
    invalidateListForMovedEntity(mapEntity);
};

/**
 * Propagates a device model change to the map: its marker's position, and an announcement for the
 * cluster icons, which snapshot appearance and can't observe the model themselves.
 */
export const registerMapEntityModelSync = (mapEntity: PositionedMapEntity) => {
    mapEntity.model.onChange?.addListener(() => {
        syncMapEntityPositionFromModel(mapEntity);
        // Outside the position sync, which stops early when nothing moved — appearance still changed.
        deviceMarkerUpdateEvent.trigger({ mapEntityId: mapEntity.id });
    }, mapEntity.uid);
};
